/**
 * Sessão do usuário logado, vinda do servidor Bun em `GET /session`.
 *
 * Equivale ao `st.user` + `st.session_state["user_roles"]` e
 * `["gitlab_username"]` do painel antigo. O JWT fica só no servidor.
 */
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

export type Papel = 'admin' | 'manager' | 'user'

/** Falha ao trocar o e-mail por JWT no backend (API fora do ar, erro HTTP etc.). */
export interface FalhaToken {
  /** Etapa em que falhou. */
  tipo: 'conexao' | 'status' | 'json' | 'sem_token'
  /** Nome da exceção, ex.: `httpx.ConnectError`. */
  excecao: string
  mensagem: string
}

export interface Sessao {
  email: string
  name: string
  /** False quando o e-mail não é @youxgroup.com.br. */
  allowed: boolean
  roles: string[]
  gitlab_username: string | null
  /** Presente quando a troca do e-mail por JWT falhou. */
  falha_token?: FalhaToken
}

/** Página atual (caminho + query), para voltar a ela depois do login. */
function paginaAtual(): string {
  return `${window.location.pathname}${window.location.search}`
}

/**
 * Tela de entrada do painel, guardando a página atual para voltar depois do
 * login (inclusive a query, ex.: o `?redirect=chamado&wid=` dos comentários).
 */
export function urlDeEntrada(destino: string = paginaAtual()): string {
  if (destino === '/' || destino.startsWith('/entrar')) return '/entrar'
  return `/entrar?next=${encodeURIComponent(destino)}`
}

/**
 * Endereço do login Google no servidor Bun (`/auth/login`), que volta para
 * `destino` depois. `trocarConta` pede ao Google a lista de contas, para
 * entrar com outra (usado quando a conta atual não tem acesso).
 */
export function urlDeLogin(destino: string = paginaAtual(), trocarConta = false): string {
  const parametros = new URLSearchParams({ next: destino })
  if (trocarConta) parametros.set('trocar', '1')
  return `/auth/login?${parametros.toString()}`
}

async function buscarSessao(): Promise<Sessao> {
  const resposta = await fetch('/session', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
  if (resposta.status === 401) {
    // Sem login: vai para a tela de entrada, que leva ao Google e volta para esta página.
    window.location.assign(urlDeEntrada())
    return new Promise<Sessao>(() => {})
  }
  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => null)) as { detail?: unknown } | null
    throw new Error(
      typeof corpo?.detail === 'string'
        ? corpo.detail
        : `Falha ao carregar a sessão (HTTP ${resposta.status}).`
    )
  }
  return (await resposta.json()) as Sessao
}

export const sessaoQuery = queryOptions({
  queryKey: ['sessao'],
  queryFn: buscarSessao,
  staleTime: Infinity,
  gcTime: Infinity,
})

/** Sessão atual (a raiz do app só renderiza as telas depois de carregá-la). */
export function useSessao(): Sessao {
  return useSuspenseQuery(sessaoQuery).data
}

/** Conjunto de papéis do usuário (ex.: `papeis.has('admin')`). */
export function usePapeis(): ReadonlySet<string> {
  return new Set(useSessao().roles)
}

/** Encerra a sessão no servidor Bun (apaga o cookie). */
async function encerrarSessao(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
}

/** Encerra a sessão e volta para a tela de entrada. */
export async function sair(): Promise<void> {
  await encerrarSessao()
  // No atalho de login de desenvolvimento a sessão volta na mesma hora: a tela
  // fica como está (como no ambiente local).
  const novaSessao = await fetch('/session', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).catch(() => null)
  if (novaSessao?.ok) return
  window.location.assign('/entrar')
}

/**
 * Sai da conta atual e abre o Google pedindo para escolher outra conta
 * (tela de "conta sem acesso"). Volta para `destino` depois do login.
 */
export async function entrarComOutraConta(destino: string = paginaAtual()): Promise<void> {
  await encerrarSessao()
  window.location.assign(urlDeLogin(destino, true))
}
